import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {righeDellaHome, sezioniDelleRighe, tuttiIGiochiDellaScheda} from '../../moduli/leggi';
import {giochi as giochiCasino, giochiPronti, descriviErrore as erroreCasino} from '../../services/casinoApi';
import {ImmagineCopertina, tono} from '../Casino/Copertina';
import {inAnteprima} from '../../skin';
import Nota from './Nota';

/**
 * GIOCHI TOP (blocco «giochiTop» della Home): una fila di schede larghe, una
 * per gioco, col nome sopra, la copertina in 16:9, il fornitore in una
 * pastiglia e il tasto «Gioca». I giochi sono i primi `quanti` della scheda
 * scelta (la prima della lista del blocco; lista vuota = la prima scheda
 * accesa di Casino'), nell'ordine della scheda: niente scelto dal sito.
 *
 * Dal computer le schede stanno in una riga sola, larghe uguali, che si
 * allarga con lo schermo; sul telefono scorrono di lato. Toccando una
 * scheda si apre il gioco nella sua sezione.
 *
 * Una scheda senza giochi, o che non c'e', non si vede: solo in anteprima
 * resta il posto con scritto perche'.
 */
export default function GiochiTop({titolo, scelte, quanti}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const sezioni = sezioniDelleRighe();
  const riga = righeDellaHome(scelte, sezioni)[0] || null;
  const chiavi = riga ? (riga.sezione === 'home' ? riga.scheda.servono : [riga.sezione]) : [];
  const chiave = chiavi.join(',');
  const [stato, setStato] = React.useState(() => {
    const pronti = chiavi.map((k) => [k, giochiPronti(k)]);
    return pronti.length && pronti.every(([, e]) => e)
      ? {carica: false, errore: '', elenchi: Object.fromEntries(pronti)}
      : {carica: !!chiavi.length, errore: '', elenchi: {}};
  });
  React.useEffect(() => {
    if (!chiave) return undefined;
    let vivo = true;
    Promise.all(chiave.split(',').map((k) => giochiCasino(k).then((elenco) => [k, elenco])))
        .then((coppie) => vivo && setStato({carica: false, errore: '', elenchi: Object.fromEntries(coppie)}))
        .catch((e) => vivo && setStato({carica: false, errore: erroreCasino(e), elenchi: {}}));
    return () => { vivo = false; };
  }, [chiave]);

  const elenchi = chiavi.map((k) => [k, stato.elenchi[k] || []]);
  const giochi = riga ? tuttiIGiochiDellaScheda(riga.scheda, elenchi.flatMap(([, e]) => e)).slice(0, Math.max(1, Number(quanti) || 5)) : [];
  const apri = (g) => {
    const k = (elenchi.find(([, e]) => e.includes(g)) || [riga.sezione])[0];
    navigate((k === 'liveCasino' ? '/live-casino' : '/casino') + '/gioco/' + encodeURIComponent(g.id));
  };
  const intestazione = String(titolo || '').trim() || t('Giochi TOP');

  if (!riga || (!stato.carica && !stato.errore && !giochi.length)) {
    if (!inAnteprima()) return null;
    return (
      <section className={'vet-blocco vet-top'} aria-label={intestazione}>
        <div className={'vet-testata'}><h2>{intestazione}</h2></div>
        <Nota tono={'spento'}>
          {!riga
            ? t('Nessuna scheda da mostrare: si sceglie dal backoffice (Moduli › Home › Giochi TOP), fra le schede di Casinò e Live casino.')
            : t('La scheda «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › {{sezione}} › Schede).', {nome: riga.scheda.nome, sezione: riga.sezione === 'liveCasino' ? t('Live casino') : t('Casinò')})}
        </Nota>
      </section>
    );
  }
  const quante = stato.carica ? Math.max(1, Number(quanti) || 5) : giochi.length;
  return (
    <section className={'vet-blocco vet-top'} aria-label={intestazione}>
      <div className={'vet-testata'}>
        <h2><span className={'vet-top-fiamma'} aria-hidden={'true'}>🔥</span>{intestazione}</h2>
      </div>
      {stato.errore && <Nota tono={'errore'}>{stato.errore}</Nota>}
      <div className={'vet-top-fila'} style={{'--vet-top-quante': quante}} aria-busy={stato.carica || undefined}>
        {stato.carica
          ? Array.from({length: quante}, (_, i) => <div key={i} className={'vet-top-scheda vet-top-vuota'} aria-hidden={'true'}><span className={'cas-luccica'}/></div>)
          : giochi.map((g) => <SchedaTop key={g.id} gioco={g} onApri={() => apri(g)} t={t}/>)}
      </div>
    </section>
  );
}

function SchedaTop({gioco, onApri, t}) {
  const [fallita, setFallita] = React.useState(false);
  const [dalFornitore, setDalFornitore] = React.useState(false);
  React.useEffect(() => { setFallita(false); setDalFornitore(false); }, [gioco.copertina]);
  const conOriginale = !!gioco.originale && gioco.originale !== gioco.copertina;
  const indirizzo = dalFornitore ? gioco.originale : gioco.copertina;
  const conImmagine = !!indirizzo && !fallita;
  return (
    <button type={'button'} className={'vet-top-scheda'} onClick={onApri} aria-label={t('Gioca a {{nome}}', {nome: gioco.nome})}>
      <span className={'vet-top-nome'}>{gioco.nome}</span>
      <span className={'vet-top-copertina' + (conImmagine ? ' con-immagine' : ' senza')} style={{'--h': tono(gioco.id)}}>
        {conImmagine
          ? <ImmagineCopertina indirizzo={indirizzo}
              onFallita={() => (conOriginale && !dalFornitore ? setDalFornitore(true) : setFallita(true))}/>
          : <span className={'cas-iniziali'}>{gioco.nome}</span>}
        {gioco.nuovo && <span className={'cas-nuovo'}>{t('Nuovo')}</span>}
        {gioco.fornitore && <span className={'vet-top-fornitore'}>{gioco.fornitore}</span>}
      </span>
      <span className={'vet-top-gioca'}>{t('Gioca')}</span>
    </button>
  );
}
